import { useMemo, useRef, useState, type FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { stepUp } from '../auth/stepUp.ts'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { useAvailability, useDefectViews, useWorkOrders } from '../data/safety.ts'
import type { AssetRow } from '../data/fleet.ts'
import { Banner, DisabledHint, Field, Pill, errorText } from '../ui/components.tsx'
import { useWriteAccess } from '../ui/hooks.ts'
import type { Pending } from '../sync/merge.ts'
import { TransportError } from '../sync/transport.ts'
import { businessCode } from '../ui/sensitive.tsx'
import { useVerifiedFactor } from './people/Factor.tsx'


/** Putting a machine back into service. Everything here is a request: the server decides. It needs a fresh second-factor code, a server nonce,
 *  every open restriction on the machine, the repaired work order(s) with return-to-service notes, the isolation attestation and a note.
 *  Online only: there is no offline release. A login with no second factor is told so BEFORE the form, with the way to Réglages (M23 item 32:
 *  the mechanic used to fill the form and be refused on submit). */
export function ReleasePanel({ asset }: { asset: Pending<AssetRow> }) {
  const { t } = useTranslation()
  const { sendAndWait, beginOnline, db } = useClub()
  const online = useEngineState().online !== false
  const access = useWriteAccess('asset.release')
  const verified = useVerifiedFactor()
  const { blockers } = useAvailability()
  const views = useDefectViews()
  const orders = useWorkOrders()
  const list = blockers(asset.id)
  const own = list.filter((b) => b.own)
  const elsewhere = list.filter((b) => !b.own)
  const [note, setNote] = useState('')
  const [attest, setAttest] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)
  const submitting = useRef(false)

  const repairState = useMemo(() => new Map(own.filter((b) => b.defect).map((b) => {
    const v = (views ?? []).find((x) => x.defect.id === b.defect!.id)
    const linked = (v?.workOrderIds ?? []).map((id) => (orders ?? []).find((w) => w.id === id)).filter(Boolean)
    const ready = linked.length > 0 && linked.every((w) => (w!.state === 'repaired' || w!.state === 'closed') && !!w!.rts_notes)
    return [b.restriction.id, ready] as const
  })), [own, views, orders])

  if (list.length === 0) return ok ? <Banner tone="ok">{ok}</Banner> : null
  if (own.length === 0) {
    return (
      <div className="flex flex-col gap-2" data-testid="release-elsewhere">
        <Banner tone="info">{t('release.viaOnly')}</Banner>
        <ul className="flex flex-wrap gap-2">{elsewhere.map((b) => b.via && <li key={b.restriction.id}><Link to="/assets/$assetId" params={{ assetId: b.via.id }} className="btn">{b.via.readable_tag}</Link></li>)}</ul>
      </div>
    )
  }
  if (!access.permitted) return <Banner tone="info">{t('release.notAllowed')}</Banner>

  const restrictions = (
    <ul className="flex flex-col gap-2">
      {own.map((b) => (
        <li key={b.restriction.id} className="card flex flex-wrap items-center justify-between gap-2">
          <span className="font-semibold">{t(`safety.reason.${b.restriction.reason_kind}`, { text: (b.restriction.reason_kind === 'critical_defect' ? b.defect?.description : b.restriction.note) || t('safety.reason.noText') })}</span>
          {b.restriction.reason_kind === 'critical_defect' && (repairState.get(b.restriction.id) ? <Pill tone="green">{t('release.repairReady')}</Pill> : <Pill tone="amber">{t('release.repairMissing')}</Pill>)}
        </li>
      ))}
    </ul>
  )
  if (verified === false) {
    return (
      <section className="flex flex-col gap-3" data-testid="release-panel-no-factor">
        <h3 className="text-lg font-bold">{t('release.title')}</h3>
        {restrictions}
        <Banner tone="warn" testId="release-no-factor">
          <p className="font-semibold">{t('release.noFactor')}</p>
          <Link to="/settings" className="btn mt-2 w-full" data-testid="release-no-factor-go">{t('release.noFactorGo')}</Link>
        </Banner>
      </section>
    )
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (submitting.current) return
    submitting.current = true; setBusy(true); setError(null); setOk(null)
    try {
      const s = await stepUp(code)
      if (s.error) { setError(s.error === 'no_factor' ? t('release.noFactor') : t('auth.mfaInvalid')); return }
      const nonce = await beginOnline()
      const v = await sendAndWait('asset.release@1', {
        asset_id: asset.id, addressed_restriction_ids: own.map((b) => b.restriction.id), note: note.trim(), isolation_attestation: attest,
      }, { nonce })
      if (v.status === 'queued') { await db.outbox.update(v.commandId, { status: 'discarded' }); setError(t('errors.network')); return }   // never leave an online-only command (and its 10-minute nonce) waiting
      if (v.status === 'applied' || v.status === 'applied_with_exception') { setOk(t('release.done')); setNote(''); setCode(''); setAttest(false); return }
      setError(errorText(t, v.code))
    } catch (ex) {
      const code = businessCode(ex)
      setError(code ? errorText(t, code) : ex instanceof TransportError && ex.kind === 'network' ? t('errors.network') : t('errors.unknown'))
    } finally { submitting.current = false; setBusy(false) }
  }

  const ready = note.trim().length > 0 && attest && /^\d{6}$/.test(code.replace(/\s/g, ''))
  return (
    <section className="flex flex-col gap-3" data-testid="release-panel">
      <h3 className="text-lg font-bold">{t('release.title')}</h3>
      <p className="meta">{t('release.help')}</p>
      {restrictions}
      {elsewhere.length > 0 && <Banner tone="warn">{t('release.alsoElsewhere')}</Banner>}
      {!online && <Banner tone="warn">{t('common.onlineOnly')}</Banner>}
      <form onSubmit={(e) => void submit(e)} className="card flex flex-col gap-3">
        <Field label={t('release.note')} hint={t('release.noteHelp')} htmlFor="release-note">
          <textarea id="release-note" className="input" rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} data-testid="release-note" />
        </Field>
        <label className="flex min-h-14 items-start gap-3 font-medium">
          <input type="checkbox" className="mt-1 size-6 shrink-0" checked={attest} onChange={(e) => setAttest(e.target.checked)} data-testid="release-attest" />
          <span>{t('release.attest')}</span>
        </label>
        <Field label={t('release.code')} hint={t('release.codeHelp')} htmlFor="release-code">
          <input id="release-code" className="input text-center text-2xl tracking-widest" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} data-testid="release-code" />
        </Field>
        {error && <Banner tone="error">{error}</Banner>}
        {ok && <Banner tone="ok">{ok}</Banner>}
        <DisabledHint show={!ready && online} testId="release-go-hint">{t('release.goHint')}</DisabledHint>
        <button className="btn btn-primary" disabled={busy || !ready || !online} data-testid="release-go">{busy ? t('common.saving') : t('release.go')}</button>
      </form>
    </section>
  )
}
